import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import api from '@/lib/axios';
import { dialogs } from '@/stores/dialog-store';

interface Tax {
  id: string;
  name: string;
  type: string;
  factor: string;
  rate: number;
  isRetention: boolean;
  isActive: boolean;
}

export function TaxesSettings() {
  const company = useAuthStore(state => state.user?.company);
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTax, setEditingTax] = useState<Tax | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'IVA',
    factor: 'Tasa',
    rate: 0.160000,
    isRetention: false,
  });

  const { data: taxes = [], isLoading } = useQuery<Tax[]>({
    queryKey: ['taxes', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const res = await api.get(`/taxes/company/${company.id}`);
      return res.data;
    },
    enabled: !!company?.id,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/taxes', { ...data, companyId: company?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] });
      setIsModalOpen(false);
      dialogs.alert('Impuesto creado exitosamente', 'Éxito');
    },
    onError: (err: any) => {
      dialogs.alert(err.response?.data?.message || 'Error al guardar el impuesto', 'Error');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: {id: string, body: any}) => api.put(`/taxes/${data.id}`, data.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] });
      setIsModalOpen(false);
      dialogs.alert('Impuesto actualizado exitosamente', 'Éxito');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/taxes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] });
      dialogs.alert('Impuesto eliminado exitosamente', 'Éxito');
    },
  });

  const handleOpenModal = (tax?: Tax) => {
    if (tax) {
      setEditingTax(tax);
      setFormData({
        name: tax.name,
        type: tax.type,
        factor: tax.factor,
        rate: tax.rate,
        isRetention: tax.isRetention,
      });
    } else {
      setEditingTax(null);
      setFormData({
        name: '',
        type: 'IVA',
        factor: 'Tasa',
        rate: 0.160000,
        isRetention: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTax) {
      updateMutation.mutate({ id: editingTax.id, body: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await dialogs.confirm('¿Estás seguro que deseas eliminar este impuesto?', { title: 'Eliminar Impuesto' });
    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <div className="p-8 text-center text-ios-gray">Cargando impuestos...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-ios-dark">Catálogo de Impuestos</h3>
          <p className="text-sm text-ios-gray">Gestiona los impuestos aplicables (IVA, IEPS, Retenciones).</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-ios-blue text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo Impuesto
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-ios-gray-4 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-ios-gray-6/30 text-ios-gray font-medium">
            <tr>
              <th className="px-6 py-4">Nombre</th>
              <th className="px-6 py-4">Tipo</th>
              <th className="px-6 py-4">Factor</th>
              <th className="px-6 py-4">Tasa/Cuota</th>
              <th className="px-6 py-4 text-center">Retención</th>
              <th className="px-6 py-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ios-gray-4">
            {taxes.map(tax => (
              <tr key={tax.id} className="hover:bg-ios-gray-6/30">
                <td className="px-6 py-4 font-medium text-ios-dark">{tax.name}</td>
                <td className="px-6 py-4">{tax.type}</td>
                <td className="px-6 py-4">{tax.factor}</td>
                <td className="px-6 py-4">{Number(tax.rate).toFixed(6)}</td>
                <td className="px-6 py-4 text-center">
                  {tax.isRetention ? <span className="text-red-500 font-medium">Sí</span> : <span className="text-ios-gray">No</span>}
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button
                    onClick={() => handleOpenModal(tax)}
                    className="p-2 text-ios-blue hover:bg-ios-blue/10 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(tax.id)}
                    className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {taxes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-ios-gray">
                  No hay impuestos configurados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold text-lg">
                {editingTax ? 'Editar Impuesto' : 'Nuevo Impuesto'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ios-dark mb-1">Nombre Descriptivo</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-ios-gray-6 border-none rounded-xl focus:ring-2 focus:ring-ios-blue"
                  placeholder="Ej. IVA 16%"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ios-dark mb-1">Tipo Impuesto</label>
                  <select
                    className="w-full px-4 py-2.5 bg-ios-gray-6 border-none rounded-xl focus:ring-2 focus:ring-ios-blue"
                    value={formData.type}
                    onChange={e => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="IVA">IVA</option>
                    <option value="IEPS">IEPS</option>
                    <option value="ISR">ISR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ios-dark mb-1">Tipo Factor</label>
                  <select
                    className="w-full px-4 py-2.5 bg-ios-gray-6 border-none rounded-xl focus:ring-2 focus:ring-ios-blue"
                    value={formData.factor}
                    onChange={e => setFormData({ ...formData, factor: e.target.value })}
                  >
                    <option value="Tasa">Tasa</option>
                    <option value="Cuota">Cuota</option>
                    <option value="Exento">Exento</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ios-dark mb-1">Valor (Tasa/Cuota)</label>
                <input
                  type="number"
                  step="0.000001"
                  required
                  className="w-full px-4 py-2.5 bg-ios-gray-6 border-none rounded-xl focus:ring-2 focus:ring-ios-blue"
                  placeholder="0.160000"
                  value={formData.rate}
                  onChange={e => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                />
                <p className="text-xs text-ios-gray mt-1">Ingresa el valor decimal (Ej. 0.160000 para 16%)</p>
              </div>

              <label className="flex items-center gap-3 p-3 bg-ios-gray-6 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded border-gray-300 text-ios-blue focus:ring-ios-blue"
                  checked={formData.isRetention}
                  onChange={e => setFormData({ ...formData, isRetention: e.target.checked })}
                />
                <div>
                  <p className="text-sm font-medium text-ios-dark">Es Retención</p>
                  <p className="text-xs text-ios-gray">Marca si es una retención de impuestos</p>
                </div>
              </label>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-ios-gray hover:bg-ios-gray-6 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-ios-blue text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {editingTax ? 'Guardar Cambios' : 'Crear Impuesto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
